/**
 * User Registration Screen Component
 *
 * This component handles new user registration with:
 * - Email and password registration
 * - Form validation for all fields
 * - Terms of service and marketing consent options
 * - Social authentication providers
 * - Success confirmation with email verification instructions
 *
 * The registration flow includes validation, duplicate email checking,
 * and Supabase authentication integration.
 */
import type { Route } from "./+types/join";

import { CheckCircle2Icon, Info } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Link, data, redirect } from "react-router";
import { z } from "zod";

import FormButton from "~/core/components/form-button";
import FormErrors from "~/core/components/form-error";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "~/core/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/core/components/ui/card";
import { Checkbox } from "~/core/components/ui/checkbox";
import { Input } from "~/core/components/ui/input";
import { Label } from "~/core/components/ui/label";
import makeServerClient from "~/core/lib/supa-client.server";
import { getPasswordErrorMessage } from "~/core/utils/password-validation";

import { SignUpButtons } from "../components/auth-login-buttons";
import { doesUserExist } from "../lib/queries.server";

/**
 * Meta function for the registration page
 *
 * Sets the page title using the application name from environment variables
 */
export const meta: Route.MetaFunction = () => {
  return [
    {
      title: `Create an account | ${import.meta.env.VITE_APP_NAME}`,
    },
  ];
};

/**
 * Form validation schema for user registration
 *
 * Uses Zod to validate:
 * - Name: Required field
 * - Email: Must be a valid email format
 * - Password: Must meet security requirements (강화된 보안 정책)
 * - Confirm Password: Must match the password field
 * - Marketing: Boolean for marketing consent (defaults to false)
 * - Terms: Boolean for terms acceptance
 *
 * Password security requirements:
 * - Minimum 8 characters
 * - At least 2 types of characters (letters, numbers, special chars)
 * - No more than 2 consecutive identical characters
 * - Not a common weak password
 *
 * The schema includes a custom refinement to ensure passwords match
 */
const joinSchema = z
  .object({
    name: z.string().min(1, { message: "이름을 입력해주세요" }),
    email: z.string().email({ message: "올바른 이메일 주소를 입력해주세요" }),
    password: z
      .string()
      .min(8, { message: "비밀번호는 최소 8자 이상이어야 합니다" })
      .refine(
        (password) => getPasswordErrorMessage(password) === true,
        (password) => ({
          message: getPasswordErrorMessage(password) as string,
        })
      ),
    confirmPassword: z
      .string()
      .min(8, { message: "비밀번호는 최소 8자 이상이어야 합니다" }),
    marketing: z.coerce.boolean().default(false),
    terms: z.coerce.boolean(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "비밀번호가 일치하지 않습니다",
    path: ["confirmPassword"],
  });

/**
 * Server action for handling user registration form submission
 *
 * This function processes the registration form data and attempts to create a new user.
 * The flow is:
 * 1. Parse and validate form data using the join schema
 * 2. Return validation errors if the data is invalid
 * 3. Verify terms of service acceptance
 * 4. Check if a user with the provided email already exists
 * 5. Create a new user with Supabase auth
 * 6. Return success or error response
 *
 * @param request - The form submission request
 * @returns Validation errors, auth errors, or success confirmation
 */
export async function action({ request }: Route.ActionArgs) {
  // Parse form data from the request
  const formData = await request.formData();
  const {
    data: validData,
    success,
    error,
  } = joinSchema.safeParse(Object.fromEntries(formData));

  // Return validation errors if form data is invalid
  if (!success) {
    return data({ fieldErrors: error.flatten().fieldErrors }, { status: 400 });
  }

  // Verify terms of service acceptance
  if (!validData.terms) {
    return data({ error: "이용약관에 동의해야 합니다" }, { status: 400 });
  }

  // Check if a user with the provided email already exists
  const userExists = await doesUserExist(validData.email);

  if (userExists) {
    return data({ error: "이미 가입된 이메일입니다." }, { status: 400 });
  }

  // Create Supabase client and attempt to sign up the user
  const [client, headers] = makeServerClient(request);
  
  // Get the current request origin to support both local and production
  // This ensures email links point to the correct environment
  const requestUrl = new URL(request.url);
  const currentOrigin = `${requestUrl.protocol}//${requestUrl.host}`;
  const redirectUrl = `${currentOrigin}/auth/confirm?type=email&next=/`;
  
  const { error: signInError, data: signUpData } = await client.auth.signUp({
    ...validData,
    options: {
      // Store additional user metadata in Supabase auth
      data: {
        name: validData.name,
        display_name: validData.name,
        marketing_consent: validData.marketing,
      },
      // Enable email confirmation but also allow immediate sign in
      // Use current request origin instead of SITE_URL to support local dev
      emailRedirectTo: redirectUrl,
    },
  });

  // Return error if user creation fails
  if (signInError) {
    return data({ error: signInError.message }, { status: 400 });
  }

  // If user was created successfully and session is available, redirect to home
  if (signUpData.session) {
    return redirect("/", { headers });
  }

  // If email confirmation is required, show success message
  return {
    success: true,
  };
}

/**
 * Registration Component
 *
 * This component renders the registration form and handles user interactions.
 * It includes:
 * - Personal information fields (name, email)
 * - Password creation with confirmation
 * - Terms of service and marketing consent checkboxes
 * - Error display for form validation and registration errors
 * - Success confirmation with email verification instructions
 * - Social registration options
 * - Sign in link for existing users
 *
 * @param actionData - Data returned from the form action, including errors or success status
 */
export default function Join({ actionData }: Route.ComponentProps) {
  // Reference to the form element for resetting after successful submission
  const formRef = useRef<HTMLFormElement>(null);

  // Get translation function
  const { t } = useTranslation();

  // State for showing password requirements
  const [showPasswordHints, setShowPasswordHints] = useState(false);

  // Reset the form when registration is successful
  useEffect(() => {
    if (actionData && "success" in actionData && actionData.success) {
      formRef.current?.reset();
      formRef.current?.blur();
    }
  }, [actionData]);
  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center">
          <CardTitle className="text-2xl font-semibold" role="heading">
            {t("auth.join.title")}
          </CardTitle>
          <CardDescription className="text-base">
            {t("auth.join.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Form
            className="flex w-full flex-col gap-5"
            method="post"
            ref={formRef}
          >
            <div className="flex flex-col items-start space-y-2">
              <Label htmlFor="name" className="flex flex-col items-start gap-1">
                {t("auth.join.name")}
              </Label>
              <Input
                id="name"
                name="name"
                required
                type="text"
                placeholder={t("auth.join.namePlaceholder")}
              />
              {actionData &&
              "fieldErrors" in actionData &&
              actionData.fieldErrors?.name ? (
                <FormErrors errors={actionData.fieldErrors.name} />
              ) : null}
            </div>
            <div className="flex flex-col items-start space-y-2">
              <Label
                htmlFor="email"
                className="flex flex-col items-start gap-1"
              >
                {t("auth.join.email")}
              </Label>
              <Input
                id="email"
                name="email"
                required
                type="email"
                placeholder={t("auth.join.emailPlaceholder")}
              />
              {actionData &&
              "fieldErrors" in actionData &&
              actionData.fieldErrors?.email ? (
                <FormErrors errors={actionData.fieldErrors.email} />
              ) : null}
            </div>
            <div className="flex flex-col items-start space-y-2">
              <div className="flex w-full items-center justify-between">
                <Label
                  htmlFor="password"
                  className="flex items-center gap-1"
                >
                  {t("auth.join.password")}
                </Label>
                <button
                  type="button"
                  onClick={() => setShowPasswordHints(!showPasswordHints)}
                  className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs transition-colors"
                >
                  <Info className="h-3 w-3" />
                  <span>보안 규칙 {showPasswordHints ? '숨기기' : '보기'}</span>
                </button>
              </div>
              
              {/* Password Security Requirements */}
              {showPasswordHints && (
                <div className="w-full rounded-md border border-border bg-accent p-3 text-xs">
                  <p className="mb-2 font-semibold text-foreground">
                    🔒 안전한 비밀번호 만들기
                  </p>
                  <ul className="space-y-1 text-foreground">
                    <li>• 최소 8자 이상</li>
                    <li>• 영문, 숫자, 특수문자 중 2가지 이상 조합</li>
                    <li>• 같은 문자 3번 이상 연속 금지 (예: aaa, 111)</li>
                    <li>• 연속된 문자/숫자 금지 (예: 1234, abcd)</li>
                    <li>• 흔한 비밀번호 금지 (예: password, 12345678)</li>
                  </ul>
                  <p className="mt-2 text-muted-foreground">
                    💡 <strong>추천:</strong> 영문 대소문자 + 숫자 + 특수문자 조합
                  </p>
                </div>
              )}

              <Input
                id="password"
                name="password"
                required
                type="password"
                placeholder={t("auth.join.passwordPlaceholder")}
                onFocus={() => setShowPasswordHints(true)}
              />
              {actionData &&
              "fieldErrors" in actionData &&
              actionData.fieldErrors?.password ? (
                <FormErrors errors={actionData.fieldErrors.password} />
              ) : null}
            </div>
            <div className="flex flex-col items-start space-y-2">
              <Label
                htmlFor="confirmPassword"
                className="flex flex-col items-start gap-1"
              >
                {t("auth.join.confirmPassword")}
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                required
                type="password"
                placeholder={t("auth.join.confirmPasswordPlaceholder")}
              />
              {actionData &&
              "fieldErrors" in actionData &&
              actionData.fieldErrors?.confirmPassword ? (
                <FormErrors errors={actionData.fieldErrors.confirmPassword} />
              ) : null}
            </div>
            <FormButton
              label={t("auth.join.createAccount")}
              className="w-full"
            />
            {actionData && "error" in actionData && actionData.error ? (
              <FormErrors errors={[actionData.error]} />
            ) : null}

            <div className="flex items-center gap-2">
              <Checkbox id="marketing" name="marketing" />
              <Label htmlFor="marketing" className="text-muted-foreground">
                {t("auth.join.marketingConsent")}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="terms" name="terms" checked />
              <Label htmlFor="terms" className="text-muted-foreground">
                <span>
                  <Link
                    to="/legal/terms-of-service"
                    viewTransition
                    className="text-muted-foreground text-underline hover:text-foreground underline transition-colors"
                  >
                    {t("auth.join.termsOfService")}
                  </Link>{" "}
                  {t("auth.join.and")}{" "}
                  <Link
                    to="/legal/privacy-policy"
                    viewTransition
                    className="text-muted-foreground hover:text-foreground text-underline underline transition-colors"
                  >
                    {t("auth.join.privacyPolicy")}
                  </Link>
                  {t("auth.join.agreeTo")}
                </span>
              </Label>
            </div>
            {actionData && "success" in actionData && actionData.success ? (
              <Alert className="bg-green-600/20 text-green-700 dark:bg-green-950/20 dark:text-green-600">
                <CheckCircle2Icon
                  className="size-4"
                  color="oklch(0.627 0.194 149.214)"
                />
                <AlertTitle>{t("auth.join.accountCreated")}</AlertTitle>
                <AlertDescription className="text-green-700 dark:text-green-600">
                  {t("auth.join.emailVerificationRequired")}
                </AlertDescription>
              </Alert>
            ) : null}
          </Form>
          <SignUpButtons />
        </CardContent>
      </Card>
      <div className="flex flex-col items-center justify-center text-sm">
        <p className="text-muted-foreground">
          {t("auth.join.alreadyHaveAccount")}{" "}
          <Link
            to="/login"
            viewTransition
            data-testid="form-signin-link"
            className="text-muted-foreground hover:text-foreground text-underline underline transition-colors"
          >
            {t("auth.join.signIn")}
          </Link>
        </p>
      </div>
    </div>
  );
}
